import { isHiGridSystemColumnId } from 'components/grid/hiGrid/core/tanstackGridColumns';
import { normalizeColumnOrder } from 'components/grid/hiGrid/core/gridColumnOrder';
import { buildBodyMergeMap } from 'components/grid/hiGrid/core/bodyMergeUtils';
import { loadGridPrefs } from 'components/grid/hiGrid/core/gridLocalPrefs';

function parseDateParts(value) {
  if (value == null || value === '') return null;
  let str = '';
  if (typeof value === 'string') str = value.trim();
  else if (value instanceof Date) str = value.toISOString().slice(0, 10);
  else str = String(value).trim();
  if (/^\d{8}$/.test(str)) {
    return { y: str.slice(0, 4), m: str.slice(4, 6), d: str.slice(6, 8) };
  }
  const withSep = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (withSep) {
    return {
      y: withSep[1],
      m: withSep[2].padStart(2, '0'),
      d: withSep[3].padStart(2, '0'),
    };
  }
  return null;
}

export function formatPrintCell(value, col) {
  if (value == null || value === '') return '';
  const type = col?.type || '';
  if (type === 'number' || typeof value === 'number') {
    const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (Number.isFinite(n)) return n.toLocaleString('ko-KR');
  }
  if (type === 'date') {
    const p = parseDateParts(value);
    if (p) return `${p.y}-${p.m}-${p.d}`;
  }
  const asDate = parseDateParts(value);
  if (asDate && /^\d{8}$/.test(String(value).trim())) {
    return `${asDate.y}-${asDate.m}-${asDate.d}`;
  }
  return String(value);
}

/**
 * 현재 그리드에 보이는 열 정의 (숨김·열순서 반영)
 */
export function getPrintColumnDefs(handle, prefsUserId = '') {
  if (!handle) return [];
  const cols = typeof handle.getDataColumns === 'function' ? handle.getDataColumns() : [];
  const prefs = loadGridPrefs(handle.id, prefsUserId);
  const hidden = new Set(prefs.hiddenColumns || []);
  const raw = (cols || [])
    .filter((c) => c && c.meta?.visible !== false && c.visible !== false)
    .map((c) => {
      const field = c.accessorKey || c.id || c.field || '';
      return {
        field,
        header: String(c.header ?? field).replace(/^\*\s*/, ''),
        type: c.meta?.type || '',
        align: c.meta?.align || (c.meta?.type === 'number' ? 'right' : 'left'),
        mergeRows: c.meta?.mergeRows === true,
        mergeGroupKey: c.meta?.mergeGroupKey || null,
        hidden: hidden.has(field),
      };
    })
    .filter((c) => c.field && !isHiGridSystemColumnId(c.field));

  const order = normalizeColumnOrder(
    prefs.columnOrder || [],
    raw.map((c) => c.field)
  ).filter((id) => !isHiGridSystemColumnId(id));
  const byField = new Map(raw.map((c) => [c.field, c]));
  return order.map((id) => byField.get(id)).filter(Boolean);
}

function resolveMergeIds(handle, colDefs, prefsUserId) {
  const prefs = loadGridPrefs(handle?.id, prefsUserId);
  if (prefs.mergeTouched) {
    return prefs.mergeEnabled ? (prefs.mergeColumnIds || []).map(String) : [];
  }
  return colDefs.filter((c) => c.mergeRows).map((c) => c.field);
}

/**
 * 양식 + 보이는 행 → 출력 모델
 */
export function buildPrintModel(handle, layout, prefsUserId = '') {
  const allCols = getPrintColumnDefs(handle, prefsUserId);
  const selectedIds = (layout?.columnIds || []).filter((id) => allCols.some((c) => c.field === id));
  const columns = (selectedIds.length ? selectedIds : allCols.map((c) => c.field))
    .map((id) => allCols.find((c) => c.field === id))
    .filter(Boolean);

  const raw = typeof handle?.getData === 'function' ? handle.getData() || [] : [];
  const rows = (raw || []).map((original, index) => ({
    index,
    original: original || {},
    cells: Object.fromEntries(
      columns.map((c) => [c.field, formatPrintCell(original?.[c.field], c)])
    ),
  }));

  const mergeIds = resolveMergeIds(handle, allCols, prefsUserId).filter((id) =>
    columns.some((c) => c.field === id)
  );
  const mergeCols = columns
    .filter((c) => mergeIds.includes(c.field))
    .map((c) => ({
      id: c.field,
      accessorKey: c.field,
      meta: { mergeRows: true, mergeGroupKey: c.mergeGroupKey },
    }));
  const mergeMap = buildBodyMergeMap(
    rows.map((r) => ({ index: r.index, original: r.original })),
    mergeCols
  );

  return {
    gridId: handle?.id || '',
    gridLabel: handle?.label || '',
    columns,
    rows,
    mergeMap,
    rowCount: rows.length,
  };
}
